/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * useTablePreferences(tableKey, columns)
 *
 * Loads a user's per-table prefs (widths / order / hidden) from the backend
 * on mount and returns:
 *   - orderedVisibleColumns: columns array, reordered + filtered by prefs
 *   - widths: {colId: px}, backfilled from column defaults
 *   - hiddenIds: Set<string>
 *   - setWidth(colId, px)
 *   - setOrder([colId, ...])
 *   - toggleVisibility(colId)
 *   - resetPrefs()
 *   - loaded (boolean; false until first fetch completes so we don't flash defaults)
 *
 * Persists changes to the backend, debounced 800 ms.
 *
 * `columns` is the canonical column definition (id, label, defaultWidth, ...).
 * Preferences are layered ON TOP — they never mutate `columns` itself.
 */
export function useTablePreferences(tableKey, columns) {
  const columnIds = columns.map(c => c.id);
  const columnsById = Object.fromEntries(columns.map(c => [c.id, c]));
  const defaultWidths = Object.fromEntries(columns.map(c => [c.id, c.defaultWidth || 160]));
  const defaultHiddenIds = new Set(columns.filter(c => c.defaultHidden).map(c => c.id));

  const [widths, setWidths] = useState(defaultWidths);
  const [order, setOrderState] = useState(columnIds);
  const [hiddenIds, setHiddenIds] = useState(defaultHiddenIds);
  const [loaded, setLoaded] = useState(false);
  const saveTimer = useRef(null);
  const currentRef = useRef({ widths, order, hiddenIds });
  const lastFailToast = useRef(null);

  // Keep ref in sync so the debounced save always reads the latest
  useEffect(() => {
    currentRef.current = { widths, order, hiddenIds };
  }, [widths, order, hiddenIds]);

  // Load prefs once on mount for this tableKey
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('fieldflow_token');
        const res = await axios.get(`${API}/api/users/me/table-prefs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const tablePrefs = res.data?.[tableKey] || {};
        const loadedWidths = { ...defaultWidths, ...(tablePrefs.widths || {}) };
        const hasPersistedOrder = Array.isArray(tablePrefs.order) && tablePrefs.order.length > 0;
        // Missing = columns added to the canonical `columns` list since
        // this user's prefs were last saved (or ALL columns, for a
        // brand-new user with nothing saved yet).
        const missingColumnIds = hasPersistedOrder
          ? columnIds.filter(id => !tablePrefs.order.includes(id))
          : columnIds;
        // Merge persisted order with the current column list. Columns
        // added since the prefs were saved are re-inserted at their
        // CANONICAL position from `columns` — not appended — so that
        // system columns (id prefixed with `_`, e.g. `_select`) stay
        // pinned to their intended slot rather than drifting to the
        // end of a user's saved order.
        const loadedOrder = hasPersistedOrder
          ? (() => {
              const persistedKnown = tablePrefs.order.filter(id => columnIds.includes(id));
              const merged = [...persistedKnown];
              for (const id of missingColumnIds) {
                const canonicalIdx = columnIds.indexOf(id);
                const insertAt = Math.min(canonicalIdx, merged.length);
                merged.splice(insertAt, 0, id);
              }
              return merged;
            })()
          : columnIds;
        setWidths(loadedWidths);
        setOrderState(loadedOrder);
        // Opt-in columns (`defaultHidden: true`, e.g. Jobs' "Equipment"
        // column) start hidden until the user explicitly shows them —
        // covers both a brand-new user (nothing saved) and an existing
        // user whose saved prefs predate this column being added (it's
        // "missing" from their order, so they never had a chance to
        // toggle it either way).
        const persistedHidden = tablePrefs.hidden;
        const loadedHidden = new Set(
          persistedHidden !== undefined
            ? persistedHidden
            : columns.filter(c => c.defaultHidden).map(c => c.id)
        );
        if (persistedHidden !== undefined) {
          for (const id of missingColumnIds) {
            if (columnsById[id]?.defaultHidden) loadedHidden.add(id);
          }
        }
        setHiddenIds(loadedHidden);
      } catch (_e) {
        // No prefs yet or fetch failed — use column defaults
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [tableKey]);

  const persist = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('fieldflow_token');
        const { widths: w, order: o, hiddenIds: h } = currentRef.current;
        await axios.put(
          `${API}/api/users/me/table-prefs`,
          { table_key: tableKey, prefs: { widths: w, order: o, hidden: [...h] } },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (e) {
        // Log so silent 4xx failures (e.g. invalid table_key) don't go
        // undetected by QA, AND show a single toast so the user knows
        // their layout didn't stick. Debounced by only toasting once
        // per 5s to avoid spam if the user keeps dragging.
        const now = Date.now();
        if (!lastFailToast.current || now - lastFailToast.current > 5000) {
          lastFailToast.current = now;
          toast.error('Column layout could not be saved. Your changes will only apply for this session.');
        }
        console.warn('[useTablePreferences] Failed to persist prefs for table', tableKey, e?.response?.status, e?.response?.data);
      }
    }, 800);
  }, [tableKey]);

  const setWidth = useCallback((colId, px) => {
    setWidths(prev => ({ ...prev, [colId]: Math.max(60, Math.min(1000, px)) }));
    persist();
  }, [persist]);

  const setOrder = useCallback((newOrder) => {
    setOrderState(newOrder);
    persist();
  }, [persist]);

  const toggleVisibility = useCallback((colId) => {
    setHiddenIds(prev => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      return next;
    });
    persist();
  }, [persist]);

  const resetPrefs = useCallback(() => {
    setWidths(defaultWidths);
    setOrderState(columnIds);
    setHiddenIds(defaultHiddenIds);
    persist();
  }, [persist]);

  // Compose the visible/ordered column list downstream components render
  const orderedVisibleColumns = order
    .map(id => columnsById[id])
    .filter(c => c && !hiddenIds.has(c.id));

  return {
    loaded,
    widths,
    order,
    hiddenIds,
    orderedVisibleColumns,
    setWidth,
    setOrder,
    toggleVisibility,
    resetPrefs,
    allColumns: columns,
  };
}
